import { useState } from 'react'
import { Icon } from '../icons'
import { Modal } from '../kit'
import { Link } from './Link'
import { PHONE_SLOTS } from './places'
import type { Place } from './places'

/**
 * The navigation: a sidebar on a desktop, a four-slot bar on a phone.
 *
 * Two elements drawn from **one list** (`placesFor`), switched at the 900px
 * breakpoint in CSS. The old bar was one element restyled, which was the right
 * call for twelve identical tabs and the wrong one now that the phone shows
 * three places and a More sheet — the two layouts genuinely differ, and the
 * list they draw from is what has to stay the same.
 */
export function Nav({
  places,
  current,
  person,
  onSignOut,
}: {
  places: readonly Place[]
  current: Place['id'] | null
  person: { name: string; email: string } | null
  onSignOut: () => void
}): JSX.Element {
  const [more, setMore] = useState(false)
  const phone = places.slice(0, PHONE_SLOTS)
  const rest = places.slice(PHONE_SLOTS)
  const restIsCurrent = rest.some((p) => p.id === current)

  return (
    <>
      <nav className="sidebar" aria-label="Main">
        <div className="brand">
          <img src="/favicon.svg" alt="" width={24} height={24} />
          Kronos
        </div>
        {places.map((p) => (
          <Link key={p.id} to={p.to} className="sidebar__link" current={p.id === current}>
            <Icon name={p.icon} size={18} />
            {p.label}
          </Link>
        ))}
        {person ? (
          <div className="sidebar__person">
            <strong>{person.name}</strong>
            <span className="muted">{person.email}</span>
            <button className="link-button" onClick={onSignOut}>
              Sign out
            </button>
          </div>
        ) : null}
      </nav>

      <nav className="phone-bar" aria-label="Main">
        {phone.map((p) => (
          <Link key={p.id} to={p.to} className="phone-bar__link" current={p.id === current}>
            <Icon name={p.icon} size={22} />
            {p.label}
          </Link>
        ))}
        <button
          className="phone-bar__link"
          aria-current={restIsCurrent ? 'page' : undefined}
          aria-haspopup="dialog"
          onClick={() => setMore(true)}
        >
          <Icon name="more" size={22} />
          More
        </button>
      </nav>

      {more ? (
        <Modal title="More" subtitle={person ? `${person.name} · ${person.email}` : undefined} onClose={() => setMore(false)}>
          <div className="more-list">
            {rest.map((p) => (
              <Link key={p.id} to={p.to} className="more-list__link" current={p.id === current} onNavigate={() => setMore(false)}>
                <Icon name={p.icon} size={20} />
                {p.label}
              </Link>
            ))}
            <button className="more-list__link" onClick={onSignOut}>
              <Icon name="signout" size={20} />
              Sign out
            </button>
          </div>
        </Modal>
      ) : null}
    </>
  )
}
